// Canonical host is www. The Amplify 301 sends apex -> www, so a canonical on
// the apex points every page through a redirect and invites Google to ignore
// it (review finding C4). Keep this in step with the redirect, not the brand.
const SITE_URL = 'https://www.dynamworks.com';

// Copy from WEBSITE_COPY_DRAFT.md section 9 (meta), set by DWEB-22. These MUST
// stay in step with the static head in public/index.html: index.html is what a
// non-JS scraper reads, these are what Helmet swaps in once React mounts, and a
// visitor who shares a link should get the same words either way.
// DWEB-61: AI named as the SUBSTRATE, not as a new offer. Nothing is
// repositioned — the differentiator is still domain translation, no service is
// renamed, and the hero is untouched. What changed is that every surface which
// SELLS (title, description, OG) previously carried zero AI signal while the
// underlying record is AI-heavy.
//
// Word order is deliberate. Both forms run ~81 characters and Google truncates
// a title near 60, so something is always cut; putting the money terms FIRST
// means "Fractional CTO & AI Consulting" survives into the visible SERP line
// and the tagline takes the truncation instead. A term past the cut still
// counts for RANKING — the crawler reads the whole tag — so nothing is lost by
// moving the tagline back, and the click-earning half is gained.
//
// "Technical Consulting" is deliberately out of the title: it is the lower-value
// phrase, and it still appears on-page as Service 2's actual name.
//
// The description was 201 chars and truncated away "Healthcare, tax, automotive,
// research" entirely. At 160 it fits.
const HOME_TITLE =
  'DynamWorks | Fractional CTO & AI Consulting — Domain Vision to Production Reality';
const HOME_DESCRIPTION =
  'Fractional CTO and AI consulting for domain-expert founders and teams. ' +
  'Carrying AI pilots past the R&D-to-production gap. Healthcare, tax, automotive, research.';

export const defaultSEO = {
  title: HOME_TITLE,
  description: HOME_DESCRIPTION,
  canonical: SITE_URL,
  openGraph: {
    type: 'website',
    locale: 'en_US',
    url: SITE_URL,
    site_name: 'DynamWorks',
    title: HOME_TITLE,
    // The draft specifies og:description === meta description.
    description: HOME_DESCRIPTION,
  },
  twitter: {
    handle: '@dynamworks',
    site: '@dynamworks',
    cardType: 'summary_large_image',
  },
};

// Keyed by FULL pathname, including the leading slash.
//
// The old lookup did `pathname.replace('/', '')` and indexed by the result, so
// `/privacy-policy` produced the key `privacy-policy` while the entry was named
// `privacy`, and `/content/x` produced `content/x`. Every route except `/` fell
// through to defaultSEO and shared one identical title and description
// (review finding S2). The `about`, `services` and `contact` entries were dead
// code — those are scroll anchors on `/`, not routes.
//
// `h1` (DWEB-104) is the heading the route RENDERS. Blog.js and
// PrivacyPolicy.js read it from here, and scripts/prerenderContent.mjs writes
// the same string into build/<route>/index.html for crawlers that do not run
// JavaScript — one string, three readers, so the pre-JS page and the mounted
// page cannot disagree. `/` has no h1 here: its heading is HERO_TITLE in
// heroContent.js, which the prerender reads directly.
export const pageSEO = {
  '/': {
    title: HOME_TITLE,
    description: HOME_DESCRIPTION,
  },
  '/blog': {
    title: 'Insights | DynamWorks — Technical Leadership & Shipping Under Constraints',
    description:
      'Writing on fractional CTO leadership, getting a proof of concept to production, ' +
      'and the engineering judgment in between — from the DynamWorks practice.',
    h1: 'Insights from Our Blog',
  },

  // DWEB-188 — the first article published ON this site.
  //
  // The four Medium posts stay on blog.dynamworks.com by the owner's decision
  // (2026-09-12); new articles go here. The route is /blog/<slug> rather than
  // a single segment because articles accumulate and the index is their
  // parent — which cost exactly one named shape in writeRoutes and one
  // enumerated pattern below, and nothing else.
  //
  // `h1` is the article's own title. The `title` is NOT the h1: it is the
  // SERP line, so it carries the brand once at the end rather than twice
  // (/blog and /work already differ from their h1 the same way). The
  // `description` is the article's deck, and BlogPost.js renders it as the
  // deck under the h1 — one string, three readers, the DWEB-104 pattern.
  '/blog/how-we-run-fractional-cto-engagements': {
    title: 'How We Run Fractional CTO Engagements | DynamWorks',
    description:
      'Design-driven governance: what the first month looks like, how decisions get made and ' +
      'written down, and how a fractional CTO engagement is meant to end.',
    h1: 'How DynamWorks runs fractional CTO engagements',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | DynamWorks',
    description:
      'What DynamWorks collects when you visit, why, and what you can do about it — including ' +
      'how the DynaMite chat assistant handles what you type.',
    h1: 'Privacy Policy',
    // The DOCX the route renders. PrivacyPolicy.js fetches it at runtime and
    // scripts/prerenderContent.mjs converts it at build time for the static
    // page — one path, so a rename cannot break one reader silently.
    document: '/content/DynamWorks-Website-Privacy-Policy.docx',
  },
  // DWEB-190. The applications as a page of their own, from the same
  // src/data/clientApps.js the homepage's apps section renders. No product is
  // named here on purpose: this file cannot import clientApps.js (importConfig
  // loads it from plain node), so a name list here would be a second copy.
  '/work': {
    title: 'Applications We Build and Operate | DynamWorks',
    description:
      'The applications DynamWorks builds and operates — what each one does, the platforms ' +
      'it runs on, and where to get it: App Store listings, product sites and source.',
    h1: 'Applications We Build and Operate',
  },

  // DWEB-105 — one indexable page per offer.
  //
  // WHY THESE KEYS ARE WRITTEN OUT RATHER THAN DERIVED. servicesConfig.js
  // owns the paths (SERVICES[].path, derived from each offer's id) and is the
  // single source of truth. This file CANNOT import it: the prerender and the
  // IndexNow submitter load seo.js from plain node through
  // scripts/lib/importConfig.mjs, which refuses a config module carrying an
  // import. So the paths live here a second time and service-pages.spec.js
  // holds the two lists equal — the DWEB-48 pattern, a second copy with a test
  // that fails when it drifts, never a second copy on trust.
  //
  // `h1` is the offer's own title, verbatim from servicesConfig.js, and the
  // spec asserts that too. The title and description here are the page's own
  // SEO copy and are deliberately NOT the offer's `description`:
  // prerenderContent.mjs forbids any offer description on a route page (the
  // duplicate-content guard DWEB-104 left behind), and renderRoute prints this
  // description as the page's lede. NO PRICE APPEARS HERE — the offer-price
  // shape may only ever live in servicesConfig.js (the DWEB-20 grep).
  '/services/fractional-cto': {
    title: 'Fractional CTO & Interim CTO Engagements | DynamWorks',
    description:
      'A fractional or interim CTO engagement that starts diagnostic: what is blocking the team, ' +
      'and whether what exists can scale. A proof of concept rather than a document.',
    h1: 'Fractional / Interim CTO',
  },
  '/services/consulting': {
    title: 'Technical Consulting & POC-to-Production Advisory | DynamWorks',
    description:
      'Technical consulting and POC-to-production advisory, billed hourly with no retainer ' +
      'required to start. Send the detail first and the call often ends with a solution.',
    h1: 'Technical Consulting & POC-to-Production Advisory',
  },
  '/services/research': {
    title: 'Applied-Research Sprints | DynamWorks',
    description:
      'An applied-research sprint on a question nobody has answered yet: fixed scope, code you ' +
      'can run yourself, and a memo saying how confident the answer is.',
    h1: 'Applied-Research Sprint',
  },
  '/services/mvp': {
    title: 'Regulated-Ready MVP Development | DynamWorks',
    description:
      'A regulated-ready MVP built so the next step is scaling it rather than rebuilding it: ' +
      'technologies that fit the problem, a modular structure, sensible running costs.',
    h1: 'Regulated-Ready MVP',
  },
  '/services/ai-receptionist': {
    title: 'AI Phone Receptionist for Small Practices & Firms | DynamWorks',
    description:
      'An AI phone receptionist for a lean practice or firm: what you do, what you offer, what ' +
      'it costs. Every price and policy it speaks traces to a document you control.',
    h1: 'AI Phone Receptionist',
  },
};

/**
 * The paths App.js actually routes, as patterns (DWEB-74).
 *
 * This exists to answer one question: is this pathname a real page, or is it
 * about to render NotFound? `pageSEO` cannot answer it — `/content/:fileName`
 * is a genuine route with no pageSEO entry, so "absent from pageSEO" would
 * wrongly condemn it.
 *
 * KEEP IN STEP WITH THE <Route> LIST IN App.js. routes.spec.js asserts the two
 * agree, so drift fails the gate rather than silently mislabelling a real page
 * as a 404.
 */
const KNOWN_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/blog$/,
  /^\/privacy-policy$/,
  /^\/work$/,
  // DWEB-105. The five slugs are ENUMERATED, not matched as /services/<any>:
  // a catch-all would make /services/nonsense a "known route", which gives a
  // 404 page a self-referencing canonical — the contradictory signalling
  // DWEB-74 removed. routes.spec.js pins /blog/extra and /privacy-policy/x as
  // unknown for the same reason, and service-pages.spec.js holds this
  // alternation equal to SERVICES[].path.
  /^\/services\/(fractional-cto|consulting|research|mvp|ai-receptionist)$/,
  // DWEB-188. The article slug is ENUMERATED for the same reason the five
  // service slugs are: /^\/blog\/[a-z0-9-]+$/ would make /blog/anything a
  // known route, which hands a 404 page a self-referencing canonical — the
  // contradictory signalling DWEB-74 removed. routes.spec.js pins /blog/extra
  // as unknown, and that assertion is left standing on purpose.
  /^\/blog\/(how-we-run-fractional-cto-engagements)$/,
  /^\/content\/[^/]+$/,
];

/** Normalise a trailing slash; React Router treats /blog/ and /blog as one. */
const normalisePath = (pathname) => {
  const p = String(pathname || '/');
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
};

export const isKnownRoute = (pathname) =>
  KNOWN_ROUTE_PATTERNS.some((re) => re.test(normalisePath(pathname)));

/** SEO for a pathname, falling back to the site default. */
export const seoForPath = (pathname) => pageSEO[pathname] || defaultSEO;
